import { Fragment, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { API_BASE, api, qs, type AuditRow } from '../lib/api'
import { Empty } from '../components/Page'
import { Card, CardBar, ConsoleHead, clock } from '../components/admin/Console'

/**
 * The audit log.
 *
 * Every content access is recorded, which is what makes a usage report for the
 * publisher a download rather than a project. The rows are mono throughout,
 * because every column is an identifier, a time or an address, and an error is
 * the one thing marked in the proof-reader's magenta.
 */

/**
 * The board's filter set, as action prefixes the API filters on.
 *
 * The count beside each chip is the server's, over the same filter as the
 * list. A tab whose number does not survive being clicked is worse than no
 * tab, so each chip asks for its own total rather than counting the page.
 */
const FILTERS: { label: string; prefix: string }[] = [
  { label: 'All', prefix: '' },
  { label: 'session', prefix: 'content_session.' },
  { label: 'ingest', prefix: 'admin.ingest.' },
  { label: 'grant', prefix: 'admin.user' },
  { label: 'flag', prefix: 'admin.feature_flag.' },
  { label: 'export', prefix: 'content.export' },
]

interface AuditPage {
  total: number
  retention_days: number
  limit: number
  offset: number
  entries: AuditRow[]
}

const LIMIT = 200

/**
 * What counts as a failure, and so takes the proof-reader's magenta.
 *
 * An administrative bypass is not a failure: it is an administrator reading
 * outside a licence, which is recorded precisely so that it is visible. Only a
 * refusal or an error is marked.
 */
const FAILURE = /error|denied|denial|failed|failure|refus|evict/i

export function AuditPage() {
  const [filter, setFilter] = useState('All')
  const active = FILTERS.find((entry) => entry.label === filter) ?? FILTERS[0]

  const page = useQuery({
    queryKey: ['admin', 'audit', active.prefix],
    queryFn: () =>
      api.get<AuditPage>(`/admin/audit${qs({ limit: LIMIT, action_prefix: active.prefix })}`),
    refetchInterval: 30_000,
  })

  // Each chip carries its own total, counted by the API over the same filter
  // the chip applies. They are cheap because the endpoint counts rather than
  // returning rows.
  const counts = useQueries({
    queries: FILTERS.map((entry) => ({
      queryKey: ['admin', 'audit', 'count', entry.prefix],
      queryFn: () =>
        api.get<AuditPage>(`/admin/audit${qs({ limit: 1, action_prefix: entry.prefix })}`),
      staleTime: 30_000,
    })),
  })

  const rows = page.data?.entries ?? []
  const retention = page.data?.retention_days ?? 0

  return (
    <>
      <ConsoleHead
        title="Audit log"
        actions={
          <a
            className="btn-secondary no-underline"
            href={`${API_BASE}/admin/audit/export.csv${qs({ action_prefix: active.prefix })}`}
          >
            Export CSV
          </a>
        }
      />

      <Card>
        <CardBar className="gap-[9px] px-[15px] py-[11px]">
          <div className="flex flex-wrap gap-[5px]">
            {FILTERS.map((entry, index) => {
              const on = entry.label === filter
              const total = counts[index]?.data?.total
              return (
                <button
                  key={entry.label}
                  type="button"
                  onClick={() => setFilter(entry.label)}
                  title={
                    total === undefined
                      ? entry.prefix || 'every action'
                      : `${total.toLocaleString()} rows under ${entry.prefix || 'every action'}`
                  }
                  className="rounded-[--radius-md] px-2 py-[3px] font-mono text-[11px]"
                  style={{
                    background: on ? 'var(--color-text)' : 'transparent',
                    color: on
                      ? 'var(--color-bg)'
                      : total === 0
                        ? 'var(--color-neutral-500)'
                        : 'var(--color-text)',
                    border: on
                      ? '1px solid var(--color-text)'
                      : '1px solid var(--color-neutral-300)',
                  }}
                >
                  {entry.label}
                </button>
              )
            })}
          </div>
          <div className="ml-auto font-mono text-[10px] uppercase tracking-[0.04em] text-ink2">
            {(page.data?.total ?? 0).toLocaleString()} rows · retained{' '}
            {retention >= 365
              ? `${Math.round(retention / 365)} years`
              : `${retention.toLocaleString()} days`}
          </div>
        </CardBar>

        {page.isLoading ? (
          <Empty>Reading the log.</Empty>
        ) : !rows.length ? (
          <Empty>
            Nothing recorded under that filter yet. Sign-ins, searches, page views and every
            administrative change land here as they happen.
          </Empty>
        ) : (
          <div className="max-h-[560px] overflow-auto">
            <div
              className="grid min-w-[820px]"
              style={{ gridTemplateColumns: '0.62fr 0.85fr 1.35fr 2fr 0.95fr' }}
            >
              {rows.map((row, index) => {
                const failed = FAILURE.test(row.action) || FAILURE.test(row.outcome)
                const cell = 'truncate px-3.5 py-2 font-mono text-[11.5px]'
                const border =
                  index < rows.length - 1
                    ? { borderBottom: '1px solid var(--color-neutral-200)' }
                    : {}
                return (
                  <Fragment key={row.id}>
                    <div className={cell} style={border} title={row.created_at}>
                      {clock(row.created_at)}
                    </div>
                    <div className={cell} style={border}>
                      {row.actor_email ?? 'system'}
                    </div>
                    <div
                      className={cell}
                      style={{
                        ...border,
                        color: failed ? 'var(--color-accent-2-700)' : 'var(--color-text)',
                      }}
                      title={`${row.action} · ${row.outcome}`}
                    >
                      {row.action}
                    </div>
                    <div className={`${cell} text-ink2`} style={border} title={detail(row)}>
                      {detail(row)}
                    </div>
                    <div className={`${cell} text-ink2`} style={border}>
                      {row.ip ?? '-'}
                    </div>
                  </Fragment>
                )
              })}
            </div>
          </div>
        )}
      </Card>

      {rows.length < (page.data?.total ?? 0) && (
        <p className="mt-2.5 font-mono text-[10px] uppercase leading-[1.7] text-ink2">
          The newest {rows.length.toLocaleString()} of {(page.data?.total ?? 0).toLocaleString()} rows
          are shown. The CSV export carries the whole filter.
        </p>
      )}
    </>
  )
}

/** What the row was about, from its object and whatever the writer recorded with it. */
function detail(row: AuditRow): string {
  const parts: string[] = []
  if (row.object_type) {
    parts.push(row.object_id ? `${row.object_type} ${row.object_id.slice(0, 8)}` : row.object_type)
  }
  for (const [key, value] of Object.entries(row.metadata ?? {})) {
    if (value === null || value === undefined || value === '') continue
    if (typeof value === 'object') continue
    parts.push(`${key} ${String(value)}`)
  }
  if (row.counter_metric) parts.push(row.counter_metric)
  return parts.join(' · ') || '-'
}
